import { forwardRef, useEffect, useState } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import withReactContent from "sweetalert2-react-content";
import { jwtDecode } from "jwt-decode";

const Popover = forwardRef(
  (
    {
      style,
      rightNavOpen,
      closeRightNav,
      rightNavRef,
      fileId,
      onRatingSubmitted,
      isAuthenticated,
    },
    ref
  ) => {
    const [allowed, setAllowed] = useState(false);
    const succesSwal = withReactContent(Swal);

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        const userRole = decoded.role;

        const validRoles = ["admin", "moderator", "educator"];
        if (!validRoles.includes(userRole)) return;
        setAllowed(true);
      }
    }, []);

    const handleDeleteAction = async (fileId) => {
      const result = await Swal.fire({
        title: "ARE YOU SURE?",

        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,

        customClass: {
          popup: "d-popup",
          title: "d-popup-title",
          icon: "d-popup-warning-icon",
        },
      });

      if (result.isConfirmed) {
        await deleteFile(fileId);
      }
    };

    const deleteFile = async (fileId) => {
      try {
        const res = await fetch(`${API_URL}/api/file/${fileId}`, {
          method: "DELETE",
        });

        const data = await res.json();

        if (res.ok) {
          // Update local UI

          Swal.fire({
            title: "FILE DELETED SUCCESSFULLY!",

            icon: "success",
            confirmButtonText: "OK",
            customClass: {
              popup: "d-popup-success",
              title: "d-popup-success-title",
              icon: "d-popup-warning-icon",
            },
          }).then(() => {
            window.location.reload();
          });
        } else {
          Swal.fire({
            title: "FAILED TO DELETE FILE!",

            icon: "error",
            customClass: {
              popup: "d-popup-error",
              title: "d-popup-error-title",
              icon: "delete-popup-error-icon",
            },
          }).then(() => {
            window.location.reload();
          });
        }
      } catch (err) {
        console.error("Error moderating document:", err);

        Swal.fire({
          title: "FAILED TO DELETE FILE!",

          icon: "error",
          customClass: {
            popup: "d-popup-error",
            title: "d-popup-error-title",
            icon: "d-popup-error-icon",
          },
        }).then(() => {
          window.location.reload();
        });
      }
    };

    const showReportPopup = async () => {
      const result = await Swal.fire({
        title: "REPORT FILE!",
        input: "text",
        inputPlaceholder: "Reason",
        showCancelButton: true,
        confirmButtonText: "Submit",
        allowOutsideClick: false, // ❌ don't close when clicking outside
        allowEscapeKey: false, // ❌ don't close when
        // pressing ESC

        customClass: {
          popup: "report-popup",
          title: "report-title",
        },
      });

      if (result.isConfirmed && result.value?.trim()) {
        console.log("User typed:", result.value);
        try {
          const reportRes = await fetch(`${API_URL}/api/reports/document`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              file_id: fileId,
              reason: result.value,
            }),
          });

          if (!reportRes.ok) {
            console.error("Server returned", reportRes.status);
          } else {
            showSuccessAlertReport();
          }
        } catch (error) {
          console.error("Network error:", error);
        }
      } else {
        console.log("No input or canceled");
      }
    };

    const showRatingPopup = () => {
      // 🛑 SAFEGUARD: prevent opening if no active file is selected
      if (!fileId) {
        Swal.fire({
          title: "No file selected",

          text: "Please click a file's menu (three dots) before rating.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }

      Swal.fire({
        title: "RATE FILE!",
        html: `
           <div id="star-container" class="rating-stars">
      <i class="fa fa-star rating-star" data-value="1"></i>
      <i class="fa fa-star rating-star" data-value="2"></i>
      <i class="fa fa-star rating-star" data-value="3"></i>
      <i class="fa fa-star rating-star" data-value="4"></i>
      <i class="fa fa-star rating-star" data-value="5"></i>
    </div>
    <button id="submit-rating" class="rating-submit-btn">
      Submit Rating
    </button>
          `,
        showConfirmButton: false,

        customClass: {
          popup: "r-popup",
          title: "r-title",
        },
        didOpen: () => {
          console.log("Rating popup opened for file:", fileId);
          const stars = Swal.getPopup().querySelectorAll("#star-container i");
          let selected = 0;

          stars.forEach((star) => {
            star.addEventListener("click", () => {
              selected = parseInt(star.getAttribute("data-value"));
              stars.forEach((s) => (s.style.color = "gray"));
              for (let i = 0; i < selected; i++) {
                stars[i].style.color = "orange";
              }
            });
          });

          const submitBtn = Swal.getPopup().querySelector("#submit-rating");
          if (submitBtn) {
            submitBtn.addEventListener("click", async () => {
              Swal.close();
              if (selected > 0) {
                try {
                  const response = await fetch(`${API_URL}/api/rate-file`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },

                    body: JSON.stringify({
                      file_id: fileId,
                      rating: selected,
                    }),
                  });

                  if (!response.ok) {
                    console.error("Server returned", response.status);
                  } else {
                    const data = await response.json();
                    console.log("Rating response:", data);
                  }

                  showSuccessAlert();

                  if (typeof onRatingSubmitted === "function") {
                    onRatingSubmitted(); //refresh the parent component
                  }
                } catch (error) {
                  console.error("Error submitting rating:", error);
                }
              }
            });
          }
        },
      });
    };

    const showSuccessAlert = () => {
      succesSwal.fire({
        title: "RATING SUBMITTED!",

        icon: "success",
        showConfirmButton: false,
        customClass: {
          popup: "success-r",
          title: "success-r-title",
          confirmButton: "success-r-button-confirmation",
        },
        timer: 2500,
      });
    };

    const showSuccessAlertReport = () => {
      succesSwal.fire({
        title: "REPORT SUBMITTED!",

        icon: "success",
        showConfirmButton: false,
        customClass: {
          popup: "report-popup-success",
          title: "report-popup-success-title",
        },
        timer: 2500,
      });
    };

    return (
      <section>
        {!allowed && (
          <div ref={ref} style={style} className="options-container">
            <a
              className="options"
              onClick={(e) => {
                e.preventDefault();
                showRatingPopup();
              }}
            >
              RATE
            </a>
          </div>
        )}
        {allowed && (
          <>
            <div ref={ref} style={style} className="options-container-auth">
              <a
                className="options-auth"
                onClick={(e) => {
                  e.preventDefault();
                  showRatingPopup();
                }}
              >
                RATE
              </a>
              <a
                className="options-auth"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  showReportPopup();
                }}
              >
                REPORT
              </a>

              <a
                className="options-auth"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteAction(fileId);
                }}
              >
                DELETE
              </a>
            </div>
          </>
        )}
      </section>
    );
  }
);

export default Popover;
